// Sign text → board glyph layout (MB.1). PURE (ENG-1): no three/DOM.
//
// Signs stored + edited their text (game/sign) but never drew it on the board.
// This turns the four text lines into lit pixels on a fixed grid the client
// rasterizes onto the sign face. A compact 3×5 bitmap font covers A–Z / 0–9 /
// space / a little punctuation; each glyph is 3 wide + 1 spacing, each line 5 tall
// + 1 leading, and each line is centred horizontally. The client maps the integer
// pixel grid to the board quad; the layout itself is what the tests pin.
import { normalizeSignText, SIGN_LINES, SIGN_LINE_MAX } from '../game/sign';

export const GLYPH_W = 3;
export const GLYPH_H = 5;
const ADVANCE = GLYPH_W + 1;       // one blank column between glyphs
const LEADING = GLYPH_H + 1;       // one blank row between lines
/** The board pixel grid the client maps onto the sign quad. */
export const SIGN_COLS = SIGN_LINE_MAX * ADVANCE; // 60
export const SIGN_ROWS = SIGN_LINES * LEADING;    // 24

// A 3×5 font: each glyph is five rows of a three-char mask ('#', else blank).
const FONT: Record<string, readonly [string, string, string, string, string]> = {
  A: ['###', '# #', '###', '# #', '# #'],
  B: ['## ', '# #', '## ', '# #', '## '],
  C: ['###', '#  ', '#  ', '#  ', '###'],
  D: ['## ', '# #', '# #', '# #', '## '],
  E: ['###', '#  ', '## ', '#  ', '###'],
  F: ['###', '#  ', '## ', '#  ', '#  '],
  G: ['###', '#  ', '# #', '# #', '###'],
  H: ['# #', '# #', '###', '# #', '# #'],
  I: ['###', ' # ', ' # ', ' # ', '###'],
  J: ['  #', '  #', '  #', '# #', '###'],
  K: ['# #', '## ', '#  ', '## ', '# #'],
  L: ['#  ', '#  ', '#  ', '#  ', '###'],
  M: ['# #', '###', '###', '# #', '# #'],
  N: ['# #', '###', '###', '###', '# #'],
  O: ['###', '# #', '# #', '# #', '###'],
  P: ['###', '# #', '###', '#  ', '#  '],
  Q: ['###', '# #', '# #', '###', '  #'],
  R: ['###', '# #', '## ', '# #', '# #'],
  S: ['###', '#  ', '###', '  #', '###'],
  T: ['###', ' # ', ' # ', ' # ', ' # '],
  U: ['# #', '# #', '# #', '# #', '###'],
  V: ['# #', '# #', '# #', '# #', ' # '],
  W: ['# #', '# #', '###', '###', '# #'],
  X: ['# #', '# #', ' # ', '# #', '# #'],
  Y: ['# #', '# #', ' # ', ' # ', ' # '],
  Z: ['###', '  #', ' # ', '#  ', '###'],
  '0': ['###', '# #', '# #', '# #', '###'],
  '1': [' # ', '## ', ' # ', ' # ', '###'],
  '2': ['###', '  #', '###', '#  ', '###'],
  '3': ['###', '  #', '###', '  #', '###'],
  '4': ['# #', '# #', '###', '  #', '  #'],
  '5': ['###', '#  ', '###', '  #', '###'],
  '6': ['###', '#  ', '###', '# #', '###'],
  '7': ['###', '  #', ' # ', ' # ', ' # '],
  '8': ['###', '# #', '###', '# #', '###'],
  '9': ['###', '# #', '###', '  #', '###'],
  '.': ['   ', '   ', '   ', '   ', ' # '],
  ',': ['   ', '   ', '   ', ' # ', '#  '],
  '!': [' # ', ' # ', ' # ', '   ', ' # '],
  '?': ['###', '  #', ' ##', '   ', ' # '],
  '-': ['   ', '   ', '###', '   ', '   '],
  ':': ['   ', ' # ', '   ', ' # ', '   '],
  "'": [' # ', ' # ', '   ', '   ', '   '],
  '/': ['  #', '  #', ' # ', '#  ', '#  '],
  '+': ['   ', ' # ', '###', ' # ', '   '],
};

/** The 5×3 lit-pixel mask for a character (space / unknown → all-blank). */
export function glyphBitmap(ch: string): boolean[][] {
  const rows = FONT[ch.toUpperCase()];
  const out: boolean[][] = [];
  for (let r = 0; r < GLYPH_H; r++) {
    const line = (rows ? rows[r] : '   ') ?? '   ';
    out.push([line[0] === '#', line[1] === '#', line[2] === '#']);
  }
  return out;
}

/** The pixel width a rendered string occupies (glyphs + inter-glyph gaps). */
export function textPixelWidth(text: string): number {
  return text.length === 0 ? 0 : text.length * ADVANCE - 1;
}

export interface SignPixel { x: number; y: number; }

/** An RGBA raster of a sign's text — the canvas-texture the client uploads onto the
 *  board quad. `SIGN_COLS × SIGN_ROWS`, lit glyph pixels opaque `fg`, rest transparent. */
export interface SignTexture { width: number; height: number; data: Uint8Array; }

/** Rasterize the four text lines into an RGBA buffer (pure — no DOM/canvas). Lit
 *  glyph pixels get the foreground colour at full alpha; everything else is clear. */
/** Glow-ink text colour, and the dim halo painted around each glyph so it reads as
 *  self-lit rather than merely pale (M22 glow ink). */
export const SIGN_GLOW_FG: readonly [number, number, number] = [236, 246, 255];
const SIGN_GLOW_HALO: readonly [number, number, number] = [96, 150, 200];

export function rasterizeSign(
  lines: readonly string[],
  fg: readonly [number, number, number] = [28, 22, 14],
  glowing = false,
): SignTexture {
  const width = SIGN_COLS, height = SIGN_ROWS;
  const data = new Uint8Array(width * height * 4); // zero = transparent
  const put = (x: number, y: number, c: readonly [number, number, number], a: number): void => {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const i = (y * width + x) * 4;
    if (data[i + 3]! >= a) return; // never dim a pixel already painted brighter
    data[i] = c[0]; data[i + 1] = c[1]; data[i + 2] = c[2]; data[i + 3] = a;
  };
  const pixels = signPixels(lines);
  // Glow ink paints a halo first, so the glyph body lands on top of it.
  if (glowing) {
    for (const { x, y } of pixels) {
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) put(x + dx, y + dy, SIGN_GLOW_HALO, 140);
    }
  }
  const body = glowing ? SIGN_GLOW_FG : fg;
  for (const { x, y } of pixels) put(x, y, body, 255);
  return { width, height, data };
}

/** How many opaque (lit) pixels a rasterized sign texture has — the render/debug proof. */
export function signTextureLitCount(tex: SignTexture): number {
  let n = 0;
  for (let i = 3; i < tex.data.length; i += 4) if (tex.data[i]! > 0) n++;
  return n;
}

/**
 * The lit pixels for the sign's four lines on the SIGN_COLS×SIGN_ROWS grid, each
 * line centred horizontally. (x grows right, y grows down.) The client maps these
 * onto the board quad; the set + its bounds are what the tests assert.
 */
export function signPixels(lines: readonly string[]): SignPixel[] {
  const norm = normalizeSignText(lines);
  const out: SignPixel[] = [];
  for (let li = 0; li < SIGN_LINES; li++) {
    const text = norm[li]!;
    const startX = Math.max(0, Math.floor((SIGN_COLS - textPixelWidth(text)) / 2));
    const baseY = li * LEADING;
    for (let ci = 0; ci < text.length; ci++) {
      const bm = glyphBitmap(text[ci]!);
      const gx = startX + ci * ADVANCE;
      for (let r = 0; r < GLYPH_H; r++) {
        for (let c = 0; c < GLYPH_W; c++) {
          if (bm[r]![c]) out.push({ x: gx + c, y: baseY + r });
        }
      }
    }
  }
  return out;
}
